import React from 'react'
import { Maybe } from '@graphql-tools/utils'
import classNames from 'classnames'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsSpan, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildJoinView/GuildEmailInput'
import { AlreadyAGuildMember } from '@/views/GuildSignupView/GuildSingleShowLayout/GuildHeroCTA/AlreadyAGuildMember'

interface GuildSharingSectionProps {
  heading?: React.ReactNode | string
  backgroundHeroImage: Maybe<string>
  paragraph: React.ReactNode | string
  hasUser?: boolean
  hideAlreadyAGuildMember?: boolean
}

export const GuildSharingSection: React.FC<GuildSharingSectionProps> = ({
  heading,
  backgroundHeroImage,
  paragraph,
  hideAlreadyAGuildMember,
  hasUser,
}) => {
  const { t } = useTranslate('guild')
  return (
    <section
      aria-label={typeof heading === 'string' ? heading : undefined}
      className="2xl:max-h-auto relative h-full min-h-screen w-full overflow-hidden"
    >
      {backgroundHeroImage && (
        <ResponsiveBackgroundImage
          src={backgroundHeroImage}
          className="pointer-events-none touch-none select-none"
          transforms="q_auto:low"
        />
      )}
      <div
        className={classNames(
          'min-h-screen w-full bg-transparent px-4 md:max-h-[800px] md:px-8 xl:mx-[40px] xl:px-4 flex pb-[120px] se:pb-[200px] pt-12',
        )}
      >
        <div className="relative flex h-full w-full flex-col-reverse items-center justify-between gap-10 lg:!flex-row 2xl:mx-auto 2xl:max-w-[1600px]">
          <div className="flex h-full w-full flex-col items-start justify-center gap-y-4 bg-transparent py-0 opacity-100 md:items-center md:gap-y-6 lg:-mt-40 lg:w-1/2 lg:items-start lg:pb-32 lg:pt-44 2xl:pl-10">
            <div>
              <div className="flex flex-col items-center gap-y-1">
                <GuildHighResShieldIcon className="invisible h-0 w-0 md:visible md:h-full md:w-fit" size={64} />
                <GuildHighResShieldIcon
                  className="invisible h-0 w-0 sm:visible sm:h-full sm:w-fit md:invisible md:h-0 md:w-0"
                  size={48}
                />
                <ParagraphSM color="white" className="hidden uppercase md:flex">
                  {t('guild', 'Guild')}
                </ParagraphSM>
              </div>
            </div>
            <div className="z-20 flex max-w-[600px] flex-col gap-y-4 md:mt-0 se:max-w-full ">
              <HeadingXS
                as={AsH1}
                weight="bold"
                className="se:photon-title-lg md:photon-heading-lg 2xl:photon-heading-xl w-full text-balance bg-transparent text-center md:text-left"
                color="white"
              >
                {heading}
              </HeadingXS>
              <ParagraphSM
                as={AsSpan}
                className="[&>p]:photon-paragraph-sm md:[&>p]:photon-paragraph-md se:[&>p]:photon-caption-sm 2xl:[&>p]:photon-paragraph-xl mb-3 w-full text-balance bg-transparent text-center md:text-left"
                color="core-gray-400"
              >
                {paragraph}
              </ParagraphSM>
            </div>
            <div className="flex w-full max-w-[600px] flex-col gap-4">
              <GuildEmailInput
                hasUser={hasUser}
                isCentered={false}
                className="w-full"
                inputContainerClassName="!px-0"
              />
              <AlreadyAGuildMember
                className={classNames('text-center text-[14px] text-core-gray-300 lg:text-left', {
                  'hidden lg:block': hideAlreadyAGuildMember,
                })}
                linkClassName="text-core-oxide relative underline cursor-pointer"
              />
            </div>
          </div>
          <div className="grow-1">
            <img
              alt="Share with Friends"
              width={200}
              src="https://images.angelstudios.com/image/upload/e_trim/v1746036541/angel-studios/Jla3P43tx11.webp"
              className="mx-auto md:-mt-20"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
